import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { secretsApi } from "@/lib/api/secrets";
import { SecretScope, SecretType, SecretMode } from "@optidev/shared-types";
import toast from "react-hot-toast";

const parseEnvContent = (content: string) => {
  const lines = content.split("\n");
  const parsed: { key: string; value: string; line: number }[] = [];
  const comments: { content: string; line: number }[] = [];
  const errors: { content: string; line: number; error: string }[] = [];

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const lineNum = index + 1;

    if (!trimmed) return;

    if (trimmed.startsWith("#")) {
      comments.push({ content: trimmed, line: lineNum });
      return;
    }

    const equalIndex = trimmed.indexOf("=");
    if (equalIndex === -1) {
      errors.push({
        content: trimmed,
        line: lineNum,
        error: "Missing = separator",
      });
      return;
    }

    const key = trimmed.substring(0, equalIndex).trim();
    const value = trimmed.substring(equalIndex + 1).trim();

    if (!key) {
      errors.push({ content: trimmed, line: lineNum, error: "Empty key" });
      return;
    }

    parsed.push({ key, value, line: lineNum });
  });

  return { parsed, comments, errors };
};

interface ImportEnvModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  scope: SecretScope;
  projectId?: string;
}

export default function ImportEnvModal({
  isOpen,
  onClose,
  onSuccess,
  scope,
  projectId,
}: ImportEnvModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [envContent, setEnvContent] = useState("");

  const { parsed, comments, errors } = parseEnvContent(envContent);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!envContent.trim()) {
      toast.error("Please paste .env content");
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const result = await secretsApi.importEnv(token, {
        scope,
        projectId,
        envContent,
        mode: SecretMode.LITE,
        type: SecretType.MASKED,
      });

      setEnvContent("");
      toast.success(
        `Imported: ${result.created} created, ${result.updated} updated, ${result.skipped} skipped`
      );
      onSuccess();
    } catch (error: any) {
      console.error("Failed to import .env:", error);
      toast.error(error.response?.data?.message || "Failed to import .env");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import from .env</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Paste your .env file content below. Values will be imported as
            masked secrets in Lite mode.
          </p>

          <div>
            <Label htmlFor="envContent">Environment content</Label>
            <Textarea
              id="envContent"
              placeholder={`# Example:
DATABASE_URL=postgresql://user:password@localhost:5432/db
API_KEY=secret123
NEXT_PUBLIC_APP_URL=https://app.example.com`}
              value={envContent}
              onChange={(e) => setEnvContent(e.target.value)}
              rows={12}
              className="font-mono text-sm mt-2"
            />
            {envContent.trim() && (
              <div className="mt-2 text-xs text-muted-foreground">
                Found: {parsed.length} secrets, {comments.length} comments
                {errors.length > 0 && (
                  <span className="text-red-600 dark:text-red-400 ml-2">
                    · {errors.length} error{errors.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}
          </div>

          {errors.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm text-red-600 dark:text-red-400">
                Please fix these errors:
              </p>
              {errors.slice(0, 3).map((error, index) => (
                <div
                  key={index}
                  className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 p-2 rounded"
                >
                  Line {error.line}: {error.error}
                </div>
              ))}
              {errors.length > 3 && (
                <div className="text-xs text-red-600 dark:text-red-400">
                  ...and {errors.length - 3} more errors
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                loading ||
                !envContent.trim() ||
                parsed.length === 0 ||
                errors.length > 0
              }
            >
              {loading
                ? "Importing..."
                : `Import ${parsed.length > 0 ? `${parsed.length} ` : ""}Secret${parsed.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
